import React, { useMemo } from 'react';
import { FilterPanel } from '@gsa/afp-component-library';
import PropTypes from 'prop-types';
import FederalStandardCodeFilterItem from './filter-items/fed-standard-code-filter';
import VehicleGroupFilterItem from './filter-items/vehicle-group-filter';
import VehicleTypeFilterItem from './filter-items/vehicle-type-filter';
import { useFederalStandards } from '../../fvs-provider';
import useFilterPanelReset from '../../../../hooks/use-filter-panel-reset';
import {
  FED_STANDARD_CODE_FILTER,
  VEHICLE_GROUP_FILTER,
  VEHICLE_TYPE_FILTER,
  FEDERAL_STANDARDS_HIERARCHY,
} from './filter-items/filter-item-constants';

const filterKeys = ['vehicleType', 'vehicleGroup', 'fedStandardCode'];

const PreBidSummaryFilters = ({ currentFilters, setFilters }) => {
  const {
    selectedYear,
    selectedOptions,
    vehicleClassificationOptions,
    applicableFvsYears,
    setFvsContextData,
  } = useFederalStandards();

  const { FilterPanel: PreBidSummaryComponentFilterPanel } = FilterPanel;

  const defaultYear = applicableFvsYears?.find(
    (year) => year.isDefault === true,
  ) ?? { value: new Date().getFullYear() };

  const filterStructure = useMemo(() => [
    {
      key: 'year',
      title: 'Contract year',
      type: 'select',
      value: selectedYear?.toString() || defaultYear?.value?.toString(),
      operator: '$or',
      options: applicableFvsYears?.map((year) => ({
        label: year.label,
        value: year.value,
      })),
      expanded: true,
      hideClear: true,
    },
    {
      title: 'Federal standards',
      key: FEDERAL_STANDARDS_HIERARCHY,
      expanded: true,
      filters: [
        {
          key: FED_STANDARD_CODE_FILTER,
          title: 'Federal Standard Code',
          component: FederalStandardCodeFilterItem,
          permanent: false,
          operator: '$exact',
          value: selectedOptions?.fedStandardCode || '',
        },
        {
          key: VEHICLE_GROUP_FILTER,
          title: 'Vehicle group',
          component: VehicleGroupFilterItem,
          permanent: false,
          operator: '$or',
          value: selectedOptions?.vehicleGroup || '',
        },
        {
          key: VEHICLE_TYPE_FILTER,
          title: 'Vehicle type',
          component: VehicleTypeFilterItem,
          permanent: false,
          operator: '$or',
          value: selectedOptions?.vehicleType || '',
        },
      ],
    },
  ], [vehicleClassificationOptions, selectedYear, applicableFvsYears, selectedOptions, defaultYear?.value]);

  const handleFiltersChange = (updatedFilters) => {
    const filterByKeys = updatedFilters?.reduce((filterObj, currentFilterObj) => {
      const result = { ...filterObj, [currentFilterObj?.key]: currentFilterObj };
      if (currentFilterObj?.filters && Array.isArray(currentFilterObj.filters)) {
        currentFilterObj.filters.forEach((nestedFilter) => {
          result[nestedFilter?.key] = { ...nestedFilter };
        });
      }
      return result;
    }, {});

    const newFilter = {
      ...currentFilters,
      value: [...updatedFilters.filter((f) => !filterKeys.includes(f.key))],
    };

    if (filterByKeys?.year?.value) {
      setFvsContextData(
        'SET_SELECTED_YEAR',
        Number.parseInt(filterByKeys?.year?.value, 10),
      );
    }

    const fedStandardCodeChanged =
      selectedOptions?.fedStandardCode !== filterByKeys?.fedStandardCode?.value &&
      filterByKeys?.fedStandardCode?.value;

    const vehicleGroupChanged =
      selectedOptions?.vehicleGroup !== filterByKeys?.vehicleGroup?.value &&
      filterByKeys?.vehicleGroup?.value;

    if (filterByKeys?.vehicleType?.value) {
      setFvsContextData('SET_SELECTED_OPTIONS', {
        fedStandardCode: filterByKeys?.fedStandardCode?.value || selectedOptions?.fedStandardCode || '',
        vehicleGroup: filterByKeys?.vehicleGroup?.value || selectedOptions?.vehicleGroup || '',
        vehicleType: filterByKeys?.vehicleType?.value,
      });
    }
    if (vehicleGroupChanged) {
      setFvsContextData('SET_SELECTED_OPTIONS', {
        fedStandardCode: filterByKeys?.fedStandardCode?.value || selectedOptions?.fedStandardCode || '',
        vehicleGroup: filterByKeys?.vehicleGroup?.value,
        vehicleType: '',
      });
    }

    if (fedStandardCodeChanged) {
      setFvsContextData('SET_SELECTED_OPTIONS', {
        fedStandardCode: filterByKeys?.fedStandardCode?.value,
        vehicleGroup: '',
        vehicleType: '',
      });
    }

    setFilters(newFilter);
  };

  // 'Reset All' keeps the contract year and clears every other filter. The
  // pre-bid summary is queried straight from the federal standards context, so
  // the selections carried over from the other FVS pages have to be cleared
  // there for the reset to reach the table.
  const { filterPanelKey, handleClearAll } = useFilterPanelReset(() => {
    setFvsContextData('SET_SELECTED_OPTIONS', {
      ...selectedOptions,
      fedStandardCode: '',
      vehicleGroup: '',
      vehicleType: '',
    });
  });

  return (
    <PreBidSummaryComponentFilterPanel
      key={`pre-bid-summary-filters-${filterPanelKey}`}
      clearButtonLabel="Reset All"
      filterStructure={filterStructure}
      model="PreBidSummary"
      order={[]}
      setQueryFiltersState={handleFiltersChange}
      handleClearAll={handleClearAll}
      showClearIcon
      retainFilters={true}
    />
  );
};

PreBidSummaryFilters.propTypes = {
  currentFilters: PropTypes.shape({
    operator: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default PreBidSummaryFilters;
